/**
 * @file shop-domain-status.ts
 * @description Статус адреса магазина и правила действий над ним (phase19.28
 * этап А; uiux-platform-shops §5, §7; PL10–PL12, SEO4; devops §5.3).
 *
 * Одна пилюля на адрес — первая неудача по порядку DNS → прокси → сертификат.
 * Прокси: 301 на https — хост заведён с Force SSL; 200 — заведён без
 * перехода на HTTPS (внимание); иначе хоста в NPM нет. Сертификат меньше чем
 * на 20 дней — внимание.
 *
 * Правила кнопок — чистые функции: «Сделать главным» только у работающего
 * включённого неосновного адреса (В11), удалить и отключить — только
 * неосновной свой домен (главный и поддомен платформы — нельзя, PL10). Страница не решает это условиями в разметке.
 */

import { hostToUnicode, normalizeHost } from "@chaiberi/shared";

import type { ShopStatusMeta } from "../shop/status-meta";
import type { ShopDomain, ShopDomainCheck } from "../api-platform-shops";

/** Сертификат «скоро истекает» — меньше этого числа дней (§2). */
export const CERT_WARN_DAYS = 20;

const DAY_MS = 86_400_000;

/** Дней до конца сертификата; нет даты — null. */
export function certDaysLeft(check: ShopDomainCheck | null | undefined, now: Date = new Date()): number | null {
  const at = check?.cert?.expiresAt;
  if (!at) return null;
  const ms = Date.parse(at) - now.getTime();
  return Number.isFinite(ms) ? Math.floor(ms / DAY_MS) : null;
}

/** Хост заведён в NPM без Force SSL (devops §5.3): признак API, иначе — 2xx по http. */
function proxyWithoutHttps(check: ShopDomainCheck): boolean {
  const proxy = check.proxy;
  if (!proxy?.ok) return false;
  if (proxy.redirectsToHttps !== undefined) return !proxy.redirectsToHttps;
  const status = proxy.httpStatus;
  return typeof status === "number" && status >= 200 && status < 300;
}

/** Пилюля результата проверки (без учёта «отключён» и «проверяем»). */
export function checkStatusMeta(check: ShopDomainCheck | null | undefined, now: Date = new Date()): ShopStatusMeta {
  if (!check) return { label: "Не проверялся", tone: "neutral" };
  if (check.dns && !check.dns.ok) {
    const ip = check.dns.ips?.[0];
    return ip ? { label: `DNS не на сервер: ${ip}`, tone: "error" } : { label: "DNS-записи не найдены", tone: "error" };
  }
  if (check.proxy && !check.proxy.ok) return { label: "Прокси не настроен", tone: "attention" };
  if (proxyWithoutHttps(check)) return { label: "Прокси без перехода на HTTPS", tone: "attention" };
  if (check.cert && !check.cert.ok) return { label: "Нет сертификата", tone: "error" };
  const days = certDaysLeft(check, now);
  if (days !== null && days < CERT_WARN_DAYS) return { label: `Сертификат истекает через ${Math.max(days, 0)} дн.`, tone: "attention" };
  return { label: "Работает", tone: "success" };
}

/**
 * Пояснение сервера к первой неудачной проверке (`dns.error` / `proxy.error` /
 * `cert.error`, ревью М2): строкой под пилюлей. Нет неудачи или текста — null.
 */
export function checkStatusDetail(check: ShopDomainCheck | null | undefined): string | null {
  if (!check) return null;
  if (check.dns && !check.dns.ok) return check.dns.error || null;
  if (check.proxy && !check.proxy.ok) return check.proxy.error || null;
  if (check.cert && !check.cert.ok) return check.cert.error || null;
  return null;
}

/** Пилюля адреса в списке доменов. */
export function domainStatusMeta(domain: ShopDomain, checking = false, now: Date = new Date()): ShopStatusMeta {
  if (checking) return { label: "Проверяем…", tone: "progress" };
  if (domain.status === "DISABLED") return { label: "Отключён", tone: "neutral" };
  return checkStatusMeta(domain.lastCheck, now);
}

/** Адрес работает: включён и все проверки прошли (условие «Сделать главным»). */
export function isDomainWorking(domain: ShopDomain, now: Date = new Date()): boolean {
  if (domain.status !== "ACTIVE") return false;
  const meta = checkStatusMeta(domain.lastCheck, now);
  // Истекающий сертификат — всё ещё работает; ошибки и «не проверялся» — нет.
  return meta.tone === "success" || meta.label.startsWith("Сертификат истекает");
}

/** Есть проблема у проверки: любая неудача или сертификат < 20 дней (§2 «Есть проблемы»). */
export function hasCheckProblem(check: ShopDomainCheck | null | undefined, now: Date = new Date()): boolean {
  if (!check) return false;
  const tone = checkStatusMeta(check, now).tone;
  return tone === "error" || tone === "attention";
}

/** Что можно сделать с адресом и почему нельзя. */
export interface DomainActionRules {
  makePrimary: { allowed: boolean; reason?: string };
  toggle: { allowed: boolean; reason?: string };
  remove: { allowed: boolean; reason?: string };
}

export function domainActionRules(domain: ShopDomain, now: Date = new Date()): DomainActionRules {
  const notWorking = "Адрес ещё не работает — посетители получили бы ошибку";
  const makePrimary = domain.isPrimary
    ? { allowed: false, reason: "Это уже главный адрес" }
    : isDomainWorking(domain, now)
      ? { allowed: true }
      : { allowed: false, reason: domain.status === "DISABLED" ? "Адрес отключён" : notWorking };
  const toggle = domain.isPrimary
    ? { allowed: false, reason: "Главный адрес отключить нельзя — сначала сделайте главным другой" }
    : domain.kind === "SUBDOMAIN"
      ? { allowed: false, reason: "Адрес платформы есть у магазина всегда" }
      : { allowed: true };
  const remove = domain.isPrimary
    ? { allowed: false, reason: "Сначала сделайте главным другой адрес" }
    : domain.kind === "SUBDOMAIN"
      ? { allowed: false, reason: "Адрес платформы удалить нельзя" }
      : { allowed: true };
  return { makePrimary, toggle, remove };
}

/** Роль адреса подписью: «Главный», «Поддомен платформы · 301», «301 → главный». */
export function domainRoleLabel(domain: ShopDomain, primaryHost: string | null): string {
  if (domain.isPrimary) return domain.kind === "SUBDOMAIN" ? "Главный · поддомен платформы" : "Главный";
  const target = primaryHost ? `301 → ${primaryHost}` : "301 на главный";
  return domain.kind === "SUBDOMAIN" ? `Поддомен платформы · ${target}` : target;
}

/** Порядок строк: главный → переадресации (свои домены) → поддомен платформы (§5). */
export function sortDomains(domains: readonly ShopDomain[]): ShopDomain[] {
  const rank = (d: ShopDomain) => (d.isPrimary ? 0 : d.kind === "CUSTOM" ? 1 : 2);
  return [...domains].sort((a, b) => rank(a) - rank(b) || a.host.localeCompare(b.host));
}

/** Что сохранится: «lavka.ru» или «магазин.рф (xn--80a…)». */
export function hostPreview(host: string): string {
  const unicode = hostToUnicode(host);
  return unicode === host ? host : `${unicode} (${host})`;
}

/** IP-адрес — привязать нельзя (normalizeHost → host_ip). */
export function isIpHost(host: string): boolean {
  return normalizeHost(host).error === "host_ip";
}
